import React from "react"
import PropTypes from "prop-types"
import { formatCurrency, formatNumber, formatPercent, formatGrowth } from "../helpers"

const StatCard = ({ icon, label, value, accent = "#3b82f6", sub }) => (
  <div
    style={{
      flex: "1 1 180px",
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: 14,
      padding: "18px 20px",
      minWidth: 0,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <div
        style={{
          width: 36, height: 36, borderRadius: 10,
          background: `${accent}1a`, color: accent,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 18, flexShrink: 0,
        }}
      >
        <i className={`mdi ${icon}`} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </span>
    </div>
    <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>{sub}</div>}
  </div>
)

StatCard.propTypes = { icon: PropTypes.string, label: PropTypes.string, value: PropTypes.node, accent: PropTypes.string, sub: PropTypes.node }

const Concentracion = ({ nivel, hhi }) => {
  const cfg = {
    bajo: { color: "#10b981", bg: "#d1fae5", label: "Baja" },
    moderado: { color: "#3b82f6", bg: "#dbeafe", label: "Moderada" },
    alto: { color: "#f59e0b", bg: "#fef3c7", label: "Alta" },
    muy_alto: { color: "#ef4444", bg: "#fee2e2", label: "Muy alta" },
  }[nivel] || { color: "#64748b", bg: "#f1f5f9", label: nivel || "—" }
  return (
    <span
      style={{
        display: "inline-block", padding: "4px 12px", borderRadius: 999,
        background: cfg.bg, color: cfg.color, fontSize: 14, fontWeight: 700,
      }}
      title={`HHI: ${hhi != null ? hhi.toFixed(2) : "—"}`}
    >
      {cfg.label}
    </span>
  )
}

Concentracion.propTypes = { nivel: PropTypes.string, hhi: PropTypes.number }

const ComparativoCard = ({ titulo, periodoActual, periodoComparado, valorActual, valorComparado, crecimientoPct }) => {
  const g = formatGrowth(crecimientoPct)
  return (
    <div
      style={{
        flex: "1 1 320px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14,
        padding: 20, borderLeft: `4px solid ${g.color}`,
      }}
    >
      <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4, marginBottom: 12 }}>
        {titulo}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>{periodoComparado || "—"}</div>
          <div style={{ fontSize: 16, color: "#94a3b8", fontWeight: 500 }}>{formatCurrency(valorComparado)}</div>
        </div>
        <div style={{ fontSize: 22, color: "#cbd5e1" }}>→</div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>{periodoActual || "—"}</div>
          <div style={{ fontSize: 22, color: "#0f172a", fontWeight: 700 }}>{formatCurrency(valorActual)}</div>
        </div>
      </div>
      <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, color: "#64748b" }}>Crecimiento</span>
        <span style={{ fontSize: 18, fontWeight: 700, color: g.color }}>
          <i className={`mdi mdi-${g.icon} me-1`} />
          {g.text}
        </span>
      </div>
    </div>
  )
}

ComparativoCard.propTypes = {
  titulo: PropTypes.string, periodoActual: PropTypes.string, periodoComparado: PropTypes.string,
  valorActual: PropTypes.number, valorComparado: PropTypes.number, crecimientoPct: PropTypes.number,
}

const ResumenTab = ({ data, alertasCount }) => {
  if (!data) return null

  const kpis = data.kpis || {}
  const comparativo = data.comparativo || {}
  const resumen = data.resumen || {}

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* KPIs principales */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <StatCard
          icon="mdi-cash-multiple"
          label="Valor total"
          value={formatCurrency(kpis.total_valor)}
          accent="#10b981"
          sub={`${formatNumber(kpis.total_transacciones)} transacciones`}
        />
        <StatCard
          icon="mdi-ticket-percent-outline"
          label="Ticket promedio"
          value={formatCurrency(kpis.ticket_promedio)}
          accent="#8b5cf6"
          sub={kpis.ticket_mediano != null ? `Mediano: ${formatCurrency(kpis.ticket_mediano)}` : null}
        />
        <StatCard
          icon="mdi-cash"
          label="Efectivo"
          value={formatPercent(kpis.pct_efectivo ?? 0)}
          accent="#f59e0b"
          sub={formatCurrency(kpis.valor_efectivo)}
        />
        <StatCard
          icon="mdi-credit-card-outline"
          label="Digital"
          value={formatPercent(kpis.pct_digital ?? 0)}
          accent="#3b82f6"
          sub={formatCurrency(kpis.valor_digital)}
        />
        <StatCard
          icon="mdi-receipt-text-remove-outline"
          label="% Notas crédito"
          value={formatPercent(kpis.pct_notas_credito ?? 0)}
          accent="#ec4899"
          sub={`${formatNumber(kpis.notas_credito_transacciones)} NCs`}
        />
        <StatCard
          icon="mdi-chart-pie"
          label="Concentración"
          value={<Concentracion nivel={kpis.nivel_concentracion} hhi={kpis.hhi_metodos} />}
          accent="#6366f1"
          sub={kpis.hhi_metodos != null ? `HHI: ${kpis.hhi_metodos.toFixed(0)}` : null}
        />
      </div>

      {/* Stats secundarias */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <StatCard
          icon="mdi-source-branch"
          label="Sucursales activas"
          value={formatNumber(kpis.total_sucursales_activas ?? resumen.total_sucursales)}
          accent="#0ea5e9"
        />
        <StatCard
          icon="mdi-credit-card-multiple-outline"
          label="Métodos activos"
          value={formatNumber(kpis.total_metodos_activos ?? resumen.total_metodos)}
          accent="#14b8a6"
        />
        <StatCard
          icon="mdi-calendar-check-outline"
          label="Días operativos"
          value={formatNumber(kpis.dias_operativos ?? resumen.dias_analizados)}
          accent="#84cc16"
        />
        <StatCard
          icon="mdi-trophy-outline"
          label="Sucursal más activa"
          value={
            <span style={{ fontSize: 15 }}>
              {resumen.sucursal_mas_activa || "—"}
            </span>
          }
          accent="#f97316"
        />
        <StatCard
          icon="mdi-alert"
          label="Alertas operacionales"
          value={
            <span style={{ color: alertasCount > 0 ? "#ef4444" : "#10b981" }}>
              {formatNumber(alertasCount)}
            </span>
          }
          accent={alertasCount > 0 ? "#ef4444" : "#10b981"}
          sub={alertasCount > 0 ? "Revisa la pestaña Alertas" : "Sin alertas"}
        />
      </div>

      {/* Comparativos */}
      {(comparativo.crecimiento_valor_vs_anterior_pct != null || comparativo.crecimiento_valor_yoy_pct != null) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          {comparativo.crecimiento_valor_vs_anterior_pct != null && (
            <ComparativoCard
              titulo="vs período anterior"
              periodoActual={comparativo.periodo_actual}
              periodoComparado={comparativo.periodo_anterior}
              valorActual={comparativo.valor_actual}
              valorComparado={comparativo.valor_anterior}
              crecimientoPct={comparativo.crecimiento_valor_vs_anterior_pct}
            />
          )}
          {comparativo.crecimiento_valor_yoy_pct != null && (
            <ComparativoCard
              titulo="vs año anterior (YoY)"
              periodoActual={comparativo.periodo_actual}
              periodoComparado={comparativo.periodo_yoy}
              valorActual={comparativo.valor_actual}
              valorComparado={comparativo.valor_yoy}
              crecimientoPct={comparativo.crecimiento_valor_yoy_pct}
            />
          )}
        </div>
      )}
    </div>
  )
}

ResumenTab.propTypes = { data: PropTypes.object, alertasCount: PropTypes.number }

export default ResumenTab
